import classnames from 'classnames'
import style from './style.module.scss'
import './mapbox-gl-custom.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useEffect, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import Map, { MapRef, Marker, NavigationControl } from 'react-map-gl'
import { Button } from '@crossfit/design-system'
import { useFormNotification } from 'hooks/useFormNotification'
import { patchAffiliate } from 'services/affiliates'
import { useSelector } from 'react-redux'
import { getAffiliate } from 'store/selectors'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string

export type ViewStateProps = {
  latitude: number
  longitude: number
  zoom: number
}

export default function AdjustPinMap() {
  const mapRef = useRef<MapRef>()
  const markerRef = useRef<mapboxgl.Marker>()
  const handleFormNotification = useFormNotification()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const affiliate = useSelector(getAffiliate)

  const [viewState, setViewState] = useState<ViewStateProps>({
    latitude: Number(affiliate.lat ?? 0.0),
    longitude: Number(affiliate.lng ?? 0.0),
    zoom: 17,
  })

  const onCancel = () => {
    markerRef.current.setLngLat({
      lng: Number(affiliate.lng ?? 0.0),
      lat: Number(affiliate.lat ?? 0.0),
    })
    setViewState({
      latitude: Number(affiliate.lat ?? 0.0),
      longitude: Number(affiliate.lng ?? 0.0),
      zoom: 17,
    })
    setIsEditing(false)
  }

  const onSubmit = async ({ lat = 0.0, lng = 0.0 }) => {
    try {
      const patchResponse = await patchAffiliate(affiliate.id, {
        lat: lat,
        lng: lng,
      })

      if (patchResponse.status < 300) {
        handleFormNotification('success')
        setIsEditing(false)
        setViewState({
          latitude: lat,
          longitude: lng,
          zoom: 17,
        })
      }
    } catch (error) {
      const errorDetail =
        JSON.parse(error?.request?.response ?? {})?.detail ??
        'Something went wrong'
      handleFormNotification('error', { message: errorDetail })
    }
  }

  useEffect(() => {
    setViewState({
      latitude: Number(affiliate.lat ?? 0.0),
      longitude: Number(affiliate.lng ?? 0.0),
      zoom: 17,
    })
    markerRef?.current?.setLngLat({
      lng: Number(affiliate.lng ?? 0.0),
      lat: Number(affiliate.lat ?? 0.0),
    })
  }, [affiliate])

  return (
    <div className={style.form}>
      <Map
        id='crossfit-adjust-marker-map'
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle='mapbox://styles/crossfit-mapbox/clj8yyto900aj01ny7mm2c0fs'
        style={{
          width: '100%',
          height: '380px',
        }}
        onMove={(e) => setViewState({ ...e.viewState })}
        {...viewState}
        latitude={viewState.latitude ?? 0}
        longitude={viewState.longitude ?? 0}
      >
        <Marker
          ref={markerRef}
          latitude={
            markerRef.current
              ? markerRef.current.getLngLat().lat
              : Number(affiliate.lat ?? 0.0)
          }
          longitude={
            markerRef.current
              ? markerRef.current.getLngLat().lng
              : Number(affiliate.lng ?? 0.0)
          }
          draggable
          onDragStart={() => setIsEditing(true)}
        >
          <button className={style.markerBtn}>
            <img src={'../location-pin.svg'} />
          </button>
        </Marker>
        <NavigationControl position='bottom-right' />
      </Map>
      <div className={classnames(style.formControls, isEditing && style.show)}>
        <Button variant='secondary' size='sm' onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button
          variant='primary'
          size='sm'
          onClick={() => {
            onSubmit({
              lat: markerRef.current.getLngLat().lat,
              lng: markerRef.current.getLngLat().lng,
            })
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
